import React from 'react';
import { useClientId } from '../hooks/useClientId';
import { useClientConfiguration } from '../hooks/useClientConfiguration';
import { LoginForm } from './LoginForm';
import { UnsupportedClientMessage } from './UnsupportedClientMessage';
import { LoadingSpinner } from './LoadingSpinner';
import { AppleSignInOption } from '../types/auth';
import './ClientAwareLoginForm.css';

interface ClientAwareLoginFormProps {
  returnUrl?: string;
  onSuccess?: (token: string, redirectUrl: string) => void;
}

/**
 * Client-aware login form that validates client_id and loads client-specific configuration
 */
export const ClientAwareLoginForm: React.FC<ClientAwareLoginFormProps> = ({
  returnUrl,
  onSuccess
}) => {
  const { clientId, isValidClient, error: clientIdError } = useClientId();
  const { configuration, isLoading: configLoading, error: configError } = useClientConfiguration(clientId);

  // Show loading spinner while loading configuration
  if (isValidClient && configLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Show error message for invalid client or configuration errors
  if (!isValidClient || clientIdError || configError) {
    return (
      <UnsupportedClientMessage
        clientId={clientId}
        error={clientIdError || configError}
      />
    );
  }

  // Show error if configuration is missing
  if (!configuration) {
    return (
      <UnsupportedClientMessage
        clientId={clientId}
        error="unsupportedClient.errors.configLoadFailed"
      />
    );
  }

  // Filter available providers based on client configuration
  const getAvailableProviders = (): string[] => {
    return configuration.availableProviders;
  };

  const availableProviders = getAvailableProviders();

  // Check if specific authentication methods are enabled
  const isEmailEnabled = availableProviders.includes('email');
  const isGoogleEnabled = availableProviders.includes('google');
  const isAppleEnabled = availableProviders.includes('apple');

  // One entry per Apple Developer team this client serves. Two entries means the user has to pick
  // the app they signed up in, since Apple gives the same person a different identity per team.
  const appleSignInOptions = configuration.appleSignInOptions;

  // If no providers are enabled, show error
  if (availableProviders.length === 0) {
    return (
      <UnsupportedClientMessage
        clientId={clientId}
        error="unsupportedClient.errors.noAuthMethods"
      />
    );
  }

  return (
    <ClientAwareLoginFormContent
      clientId={clientId!}
      returnUrl={returnUrl}
      onSuccess={onSuccess}
      isEmailEnabled={isEmailEnabled}
      isGoogleEnabled={isGoogleEnabled}
      isAppleEnabled={isAppleEnabled}
      appleSignInOptions={appleSignInOptions}
      availableProviders={availableProviders}
    />
  );
};

interface ClientAwareLoginFormContentProps {
  clientId: string;
  returnUrl?: string;
  onSuccess?: (token: string, redirectUrl: string) => void;
  isEmailEnabled: boolean;
  isGoogleEnabled: boolean;
  isAppleEnabled: boolean;
  appleSignInOptions?: AppleSignInOption[];
  availableProviders: string[];
}

/**
 * Internal component that renders the actual login form with client-specific providers
 */
const ClientAwareLoginFormContent: React.FC<ClientAwareLoginFormContentProps> = ({
  clientId,
  returnUrl,
  onSuccess,
  isEmailEnabled,
  isGoogleEnabled,
  isAppleEnabled,
  appleSignInOptions,
  availableProviders
}) => {
  // Enhanced onSuccess callback that includes client_id
  const handleSuccess = (token: string, redirectUrl: string) => {
    // Add client_id to the success callback for backend processing
    if (onSuccess) {
      onSuccess(token, redirectUrl);
    }
  };

  return (
    <div className="client-aware-login">
      {/* Enhanced LoginForm with client-specific configuration */}
      <EnhancedLoginForm
        clientId={clientId}
        returnUrl={returnUrl}
        onSuccess={handleSuccess}
        isEmailEnabled={isEmailEnabled}
        isGoogleEnabled={isGoogleEnabled}
        isAppleEnabled={isAppleEnabled}
        appleSignInOptions={appleSignInOptions}
        availableProviders={availableProviders}
      />
    </div>
  );
};

interface EnhancedLoginFormProps {
  clientId: string;
  returnUrl?: string;
  onSuccess?: (token: string, redirectUrl: string) => void;
  isEmailEnabled: boolean;
  isGoogleEnabled: boolean;
  isAppleEnabled: boolean;
  appleSignInOptions?: AppleSignInOption[];
  availableProviders: string[];
}

/**
 * Enhanced LoginForm that respects client-specific provider configuration
 */
const EnhancedLoginForm: React.FC<EnhancedLoginFormProps> = ({
  clientId,
  returnUrl,
  onSuccess,
  isEmailEnabled,
  isGoogleEnabled,
  isAppleEnabled,
  appleSignInOptions
}) => {
  // For now, we'll use the existing LoginForm but pass client-aware props
  // The LoginForm will need to be updated to respect these provider flags
  // This is a simplified implementation that maintains compatibility

  return (
    <LoginForm
      clientId={clientId}
      returnUrl={returnUrl}
      onSuccess={onSuccess}
      isEmailEnabled={isEmailEnabled}
      isGoogleEnabled={isGoogleEnabled}
      isAppleEnabled={isAppleEnabled}
      appleSignInOptions={appleSignInOptions}
    />
  );
};
